/**
 * Creating and moving objects: the object-identity rule (CR 124; docs/design/03 §3.1).
 *
 * - Moving into or out of a non-board zone makes a **new incarnation**: a new id, no damage,
 *   counters, statuses, attachments, links or reveals (CR 124.1). Hidden ids therefore never correlate
 *   with public ones (05 §5).
 * - Moving between board locations keeps the object and its state (CR 448.2). Attached gear moves
 *   with its top-most card (CR 719.3); when the top-most card leaves the board its attachments are
 *   detached where they are (CR 719.5, 436.4.b).
 * - A token put into a non-board zone other than the chain ceases to exist (CR 186.1).
 *
 * These helpers mutate a draft and never check permissions; command handlers do that first.
 */
import { invariant } from '../../kernel/errors';
import { deepCloneJson } from '../../kernel/json';
import type { GameState } from '../state/game-state';
import type { CardId, ObjectId, PlayerId, PrintingId } from '../state/ids';
import { cardRefOf, isToken, type GameObject, type ObjectCategory } from '../state/objects';
import { type DeckPosition, ZONE_INFO, isBoardZone, sameZone, type ZoneRef } from '../state/zones';
import { newChainItemId, newObjectId, nextTimestamp } from './alloc';
import { attachmentsOf, getObject } from './queries';
import { detachFromZone, insertIntoList, zoneList } from './zone-lists';

/** Everything needed to create an object. */
export interface NewObject {
  category: ObjectCategory;
  isToken: boolean;
  cardId: CardId;
  printingId: PrintingId | null;
  owner: PlayerId;
  controller: PlayerId;
  zone: Exclude<ZoneRef, { kind: 'chain' }>;
  exhausted?: boolean;
}

/** Creates a new object and appends it to its zone list (or places it at `position` in a deck). */
export function createObject(draft: GameState, init: NewObject, position?: DeckPosition): GameObject {
  const id = newObjectId(draft);
  const object: GameObject = {
    id,
    category: init.category,
    isToken: init.isToken,
    cardId: init.cardId,
    printingId: init.printingId,
    owner: init.owner,
    controller: init.controller,
    zone: init.zone,
    timestamp: nextTimestamp(draft),
    exhausted: init.exhausted ?? false,
    damage: 0,
    buffs: 0,
    counters: {},
    statuses: [],
    attachedTo: null,
    faceDown: init.zone.kind === 'facedown',
    revealedTo: [],
    links: {},
  };
  draft.objects[id] = object;
  if (init.category !== 'battlefield') {
    insertIntoList(zoneList(draft, init.zone), id, position, ZONE_INFO[init.zone.kind].ordered);
  }
  return object;
}

/** How an object arrives in its new zone. */
export interface RelocateOptions {
  /** Position in an ordered zone (decks). Defaults to the bottom. */
  position?: DeckPosition;
  /** Exhausted state on arrival. New incarnations default to ready; board moves keep their state. */
  exhausted?: boolean;
  /** Controller on the board or chain (the acting player). Non-board zones always use the owner. */
  controller?: PlayerId;
  /**
   * Make a new incarnation even for a move within one secret zone (a card recycled from the top of its
   * Main Deck to the bottom): its old id, and what was revealed of it, stay behind (ADR-0008).
   */
  fresh?: boolean;
}

/** What `relocate` did. */
export interface Relocation {
  /** A copy of the object as it was before the move (for events: its old zone and visibility). */
  before: GameObject;
  /** The object after the move (new id for a new incarnation), or `null` when a token ceased to exist. */
  after: GameObject | null;
  newIncarnation: boolean;
}

/**
 * Moves an object to `to`, applying the identity rule. Battlefield cards never move (CR 170.4).
 * Moving to the chain puts the card on it as a manual card item (tier 1/2 stack area).
 */
export function relocate(draft: GameState, id: ObjectId, to: ZoneRef, options: RelocateOptions = {}): Relocation {
  const object = getObject(draft, id);
  invariant(object.category !== 'battlefield', 'Battlefields never move (CR 170.4)');
  const before = deepCloneJson(object);
  const from = object.zone;
  const sameZoneMove = sameZone(from, to);
  const newIncarnation =
    (options.fresh === true && !isBoardZone(to)) || (!sameZoneMove && (!isBoardZone(from) || !isBoardZone(to)));

  detachFromZone(draft, object);

  // Attachments of the moved card (CR 719.3, 719.5): carried along on board moves, detached otherwise.
  const carried: GameObject[] = [];
  const carries = !newIncarnation && (to.kind === 'base' || to.kind === 'battlefield');
  for (const attachment of attachmentsOf(draft, id)) {
    if (carries) carried.push(attachment);
    else attachment.attachedTo = null;
  }
  // A card that moves on its own is no longer attached to anything.
  object.attachedTo = null;

  if (isToken(object) && !isBoardZone(to) && to.kind !== 'chain') {
    Reflect.deleteProperty(draft.objects, id);
    return { before, after: null, newIncarnation: true };
  }

  const controller = isBoardZone(to) || to.kind === 'chain' ? (options.controller ?? object.controller) : object.owner;
  let target: GameObject;
  if (newIncarnation) {
    Reflect.deleteProperty(draft.objects, id);
    const newId = newObjectId(draft);
    target = {
      id: newId,
      category: object.category,
      isToken: object.isToken,
      cardId: object.cardId,
      printingId: object.printingId,
      owner: object.owner,
      controller,
      zone: to,
      timestamp: nextTimestamp(draft),
      exhausted: options.exhausted ?? false,
      damage: 0,
      buffs: 0,
      counters: {},
      statuses: [],
      attachedTo: null,
      faceDown: to.kind === 'facedown',
      revealedTo: [],
      links: {},
    };
    draft.objects[newId] = target;
  } else {
    target = object;
    target.zone = to;
    target.controller = controller;
    target.faceDown = to.kind === 'facedown';
    if (options.exhausted !== undefined) target.exhausted = options.exhausted;
  }

  if (to.kind === 'chain') {
    draft.chain.push({
      id: newChainItemId(draft),
      kind: isToken(target) ? 'token' : 'card',
      object: target.id,
      source: target.id,
      sourceCard: cardRefOf(target),
      controller: target.controller,
      state: 'finalized',
      manual: true,
      origin: from,
      rules: null,
    });
  } else {
    insertIntoList(zoneList(draft, to), target.id, options.position, ZONE_INFO[to.kind].ordered);
  }

  for (const attachment of carried) {
    detachFromZone(draft, attachment);
    attachment.zone = to;
    if (to.kind !== 'chain') insertIntoList(zoneList(draft, to), attachment.id, undefined, false);
  }

  return { before, after: target, newIncarnation };
}

/** Deletes a token (or any object) from the game entirely, removing it from its zone (CR 186.1, 652.3). */
export function removeFromGame(draft: GameState, id: ObjectId): GameObject {
  const object = getObject(draft, id);
  const before = deepCloneJson(object);
  for (const attachment of attachmentsOf(draft, id)) attachment.attachedTo = null;
  detachFromZone(draft, object);
  Reflect.deleteProperty(draft.objects, id);
  return before;
}
